import { Component, computed, effect, inject, Injector, Input, OnInit, AfterViewInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, BaseModalComponent, EnumValues } from '@card-software/library';
import { ApiResponse, MasterData, Site, SiteService } from '@card-software/nexus-utilities';
import _ from 'lodash';
import { of, switchMap } from 'rxjs';
import { SelectItemGroup, TreeNode } from 'primeng/api';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { Certification } from '../../../models/certification';
import { Fabric } from '../../../models/fabric/fabric';
import { Greige } from '../../../models/greige/greige';
import { ItemMaster, ItemCategory, ItemRelationship, ItemRelationshipType, SiteProperty, Uom } from '../../../models/item-master';
import { FinancialAccount } from '../../../models/finance/financial-account';
import { FinancialAccountProxyService } from '../../../proxies/finance/financial-account.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { environment } from '../../../../environments/environment';
import { DataMasterAdminGroup } from '../../../models/authorization/enum/user-group.enum';
import { InventoryType } from '../../../models/inventory/inventory-type';
import { ItemMasterProposal, ItemMasterProposalStatus } from '../../../models/item-master-proposal';
import { MatchingItemMasterCommand, Yarn, YarnCodeWithCertAndItemMaster } from '../../../models/yarns/yarn';
import { Translation } from '../../../models/Translations';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { GreigeProxyService } from '../../../proxies/item/greige.proxy.service';
import { InventoryTypeProxyService } from '../../../proxies/inventory/inventory-type.proxy.service';
import { ItemCategoryProxyService } from '../../../proxies/item/item-category.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ProcurementItemCategoryProxyService } from '../../../proxies/order/procurement-item-category.proxy.service';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { ProductRecipeProxyService } from '../../../proxies/item/product-recipe.proxy.service';
import { ProductRecipe } from '../../../models/product-recipe/product-recipe';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { GlobalMessageUtil, getLocalizedValue } from '@card-software/nexus-utilities';
import { MasterDataActionUtil } from '../../../utilities/master-data-action-util';

@Component({
  selector: 'app-item-master-modal',
  templateUrl: './item-master-modal.component.html',
  styleUrl: './item-master-modal.component.scss',
})
export class ItemMasterModalComponent extends BaseModalComponent<ItemMaster | null> implements OnInit, AfterViewInit {
  loading = 0;
  @Input() canEdit = false;
  @Input() itemMaster: ItemMaster | null = null;
  @Input() certifications: Certification[] = [];
  @Input() itemMasterProposal: ItemMasterProposal | null = null;
  @Input() duplicateItemMaster = false;
  @Input() uoms: EnumValues = [];
  @Input() itemRequestReference: string | null = null;

  disableFileSection = true;
  uploadedFiles: File[] = [];
  selectedFiles: File[] = [];
  isUploading = false;

  submittedForm = false;
  baseYarn: Yarn | null = null;
  baseFabric: Fabric | null = null;
  baseGreige: Greige | null = null;
  baseProductRecipe: ProductRecipe | null = null;
  // Per-site option caches. Dropdowns inside each accordion panel use these via the
  // helpers below — same item can have different category / inventory type per site.
  inventoryTypeOptionsBySite: Record<string, InventoryType[]> = {};
  allItemCategoriesBySite: Record<string, ItemCategory[]> = {};
  itemCategorySuggestionsBySite: Record<string, ItemCategory[]> = {};
  groupedLvl2ItemCategoriesBySite: Record<string, SelectItemGroup[]> = {};
  private loadedInventoryTypeSites = new Set<string>();
  private loadedItemCategorySites = new Set<string>();
  statuses: EnumValues;
  helpCreateItem = false;

  spanishTranslate: FormControl<boolean> = new FormControl<boolean>(false, {
    nonNullable: true,
  });
  selectedCertifications: FormControl<Certification[]> = new FormControl<Certification[]>([], { nonNullable: true });

  supportingItemMasters: ItemMaster[] = [];

  itemMasterFormGroup: FormGroup = new FormGroup({
    itemDisplayName: new FormGroup({
      en: new FormControl('', Validators.required),
      es: new FormControl(''),
    }),
    itemLongDisplayName: new FormGroup({
      en: new FormControl(''),
      es: new FormControl(''),
    }),
    itemDescription: new FormGroup({
      en: new FormControl('', Validators.required),
      es: new FormControl(''),
    }),
    unitOfMeasure: new FormControl(null, [Validators.required]),
    itemClassificationCode: new FormControl(''),
    status: new FormControl(0),
    siteIds: new FormControl<string[]>([], {
      nonNullable: true,
      validators: [ItemMasterModalComponent.atLeastOneSiteValidator],
    }),
    siteLocalNames: new FormArray<FormGroup>([]),
  });

  expandedSiteIds: string[] = [];
  siteLocalNameGroups: { siteId: string; formGroup: FormGroup }[] = [];

  get canAssignSites(): boolean {
    return AuthService.hasRole(DataMasterAdminGroup.ItemSiteAssigner);
  }

  get availableSites(): Site[] {
    return SiteService.getSites();
  }

  get siteLocalNamesArray(): FormArray<FormGroup> {
    return this.itemMasterFormGroup.get('siteLocalNames') as FormArray<FormGroup>;
  }

  getSiteName(siteId: string): string {
    return this.availableSites.find((s) => s.id === siteId)?.name ?? siteId;
  }

  private static atLeastOneSiteValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return Array.isArray(value) && value.length > 0 ? null : { required: true };
  }

  private buildSitePropertyGroup(siteId: string, initial?: SiteProperty): FormGroup {
    return new FormGroup({
      siteId: new FormControl(siteId, { nonNullable: true }),
      en: new FormControl(initial?.itemLocalName?.en ?? '', { nonNullable: true }),
      es: new FormControl(initial?.itemLocalName?.es ?? '', { nonNullable: true }),
      inventoryTypeReference: new FormControl(initial?.inventoryTypeReference ?? '', [Validators.required]),
      itemCategoryCode: new FormControl(initial?.itemCategoryCode ?? '', [Validators.required]),
      itemLowestSubCategoryCode: new FormControl(initial?.itemLowestSubCategoryCode ?? ''),
    });
  }

  private syncSiteLocalNames(currentSiteIds: string[]): void {
    const array = this.siteLocalNamesArray;
    const existingBySiteId = new Map<string, FormGroup>();
    for (const group of array.controls) {
      const siteId = group.get('siteId')?.value;
      if (siteId) existingBySiteId.set(siteId, group);
    }

    const sourceProps = this.itemMaster?.siteProperties ?? [];

    array.clear({ emitEvent: false });
    const groups: { siteId: string; formGroup: FormGroup }[] = [];
    for (const siteId of currentSiteIds) {
      const existing = existingBySiteId.get(siteId);
      if (existing) {
        array.push(existing, { emitEvent: false });
        groups.push({ siteId, formGroup: existing });
      } else {
        const fromItem = sourceProps.find((sp) => sp.siteId === siteId);
        const newGroup = this.buildSitePropertyGroup(siteId, fromItem);
        array.push(newGroup, { emitEvent: false });
        groups.push({ siteId, formGroup: newGroup });
      }
      // Lazy-load dropdown options scoped to this specific site so the panel
      // shows only the categories / inventory types valid for that site.
      this.loadInventoryTypesForSite(siteId);
      this.loadItemCategoriesForSite(siteId);
    }

    this.siteLocalNameGroups = groups;
    this.expandedSiteIds = [...currentSiteIds];
  }

  getInventoryTypeOptions(siteId: string | null | undefined): InventoryType[] {
    return siteId ? (this.inventoryTypeOptionsBySite[siteId] ?? []) : [];
  }

  getItemCategorySuggestions(siteId: string | null | undefined): ItemCategory[] {
    return siteId ? (this.itemCategorySuggestionsBySite[siteId] ?? []) : [];
  }

  getCategoryLowestSubCategoriesBySite(siteId: string | null | undefined, parentCode: string | null | undefined): SelectItemGroup[] {
    if (!siteId || !parentCode) return [];
    const grouped = this.groupedLvl2ItemCategoriesBySite[siteId] ?? [];
    const all = this.allItemCategoriesBySite[siteId] ?? [];
    return grouped.filter((ic) => all.find((cat) => cat.code === ic.value)?.parentCode === parentCode);
  }

  private loadInventoryTypesForSite(siteId: string): void {
    if (this.loadedInventoryTypeSites.has(siteId)) return;
    this.loadedInventoryTypeSites.add(siteId);
    this.inventoryTypeProxyService.getAllInventoryType(siteId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.inventoryTypeOptionsBySite[siteId] = response.data.map((inventoryType) => ({
            ...inventoryType,
            optionDisplayName: this.localeHelperService.getLocalizedValue(inventoryType.name),
          }));
          this.applyProductRecipeDefaults();
        }
      },
      error: () => {
        this.loadedInventoryTypeSites.delete(siteId);
      },
    });
  }

  private loadItemCategoriesForSite(siteId: string): void {
    if (this.loadedItemCategorySites.has(siteId)) return;
    this.loadedItemCategorySites.add(siteId);
    this.itemCategoryProxyService.getAllItemsCategories(siteId).subscribe({
      next: (response) => {
        if (!response.success || !response.data) return;
        const allCategories = response.data.filter((ic) => ic.isActive);
        this.allItemCategoriesBySite[siteId] = allCategories;
        this.itemCategorySuggestionsBySite[siteId] = allCategories
          .filter((ic) => ic.level === 1)
          .map(
            (ic) =>
              ({
                ...ic,
                icFieldToDisplay: `${ic.code} - ${this.localeHelperService.getLocalizedValue(ic.name)}`,
              }) as ItemCategory,
          );
        this.groupedLvl2ItemCategoriesBySite[siteId] = allCategories
          .filter((ic) => ic.level === 2)
          .map((ic) => ({
            value: ic.code,
            label: `${ic.code} - ${this.localeHelperService.getLocalizedValue(ic.name)}`,
            items: this.getChildrenCategoriesForSite(
              siteId,
              ic.code,
              `${ic.code} - ${this.localeHelperService.getLocalizedValue(ic.name)}`,
            ),
          }));

        // Proposal flow: seed itemCategoryCode for the panel matching this site.
        if (this.helpCreateItem) {
          const itemCat = allCategories.find((ic) => ic.mongoDBCollection === this.itemMasterProposal?.mongoDBCollection && ic.level === 1);
          if (itemCat) {
            const groupForSite = this.siteLocalNamesArray.controls.find((g) => g.get('siteId')?.value === siteId);
            groupForSite?.get('itemCategoryCode')?.setValue(itemCat.code);
          }
        }
      },
      error: () => {
        this.loadedItemCategorySites.delete(siteId);
      },
    });
  }

  private getChildrenCategoriesForSite(
    siteId: string,
    parentCode: string,
    label: string,
  ): { label: string; value: string; index?: number }[] {
    const all = this.allItemCategoriesBySite[siteId] ?? [];
    return [
      { label, value: parentCode, index: 0 },
      ...all
        .filter((ic) => ic.parentCode === parentCode)
        .map((sc) => ({
          value: sc.code,
          label: `${sc.code} - ${this.localeHelperService.getLocalizedValue(sc.name)}`,
        })),
    ];
  }

  yarnCodesWithCertAndItemMaster: YarnCodeWithCertAndItemMaster[] = [];

  procurementItemCategoryReference = new FormControl<string | null>(null);
  procurementItemCategoryOptions: { id: string; name: string }[] = [];

  // GL (financial) accounts this item can post to on AP documents. Persisted as NumberedDocumentKey
  // (reference + number); `name` is display-only. The chart of accounts is shown as a checkbox tree
  // table scoped to the selected site's financial company; only posting accounts are persisted —
  // checking a header account checks the posting accounts under it.
  // Feature-flagged: the GL Accounts section is hidden in environments where the flag is off (prod).
  showGlAccountsSection = environment.featureFlags.itemMasterGlAccounts;
  glAccountTreeNodes = signal<TreeNode[]>([]);
  loadingGlAccountTree = signal(false);
  // Items that already have accounts open showing only those (easier to scan); toggle off to reveal the
  // full chart-of-accounts tree for adding/removing.
  showSelectedGlOnly = signal(false);
  private glAccountNodeById = new Map<string, TreeNode>();
  glAccounts = signal<{ reference: string; number: string; name: string }[]>([]);

  // "Show selected only" only applies when something is actually selected — with nothing selected
  // there's nothing to prune to, so we fall back to the full tree and disable the toggle.
  effectiveShowSelectedGlOnly = computed(() => this.showSelectedGlOnly() && this.glAccounts().length > 0);

  // The tree actually rendered: the full chart, or — when "show selected only" is on — pruned to the
  // selected posting accounts plus the ancestor nodes needed to keep the hierarchy intact.
  displayedGlAccountTreeNodes = computed<TreeNode[]>(() => {
    if (!this.effectiveShowSelectedGlOnly()) return this.glAccountTreeNodes();
    const selectedIds = new Set(this.glAccounts().map((g) => g.reference));
    return this.pruneGlTreeToSelected(this.glAccountTreeNodes(), selectedIds);
  });

  // Checkbox state per account id, derived from `glAccounts` over the full chart (never the pruned view),
  // so a header account reads checked/partial from all of its posting accounts in either view.
  glAccountCheckStates = computed(() => {
    const selectedIds = new Set(this.glAccounts().map((g) => g.reference));
    const states = new Map<string, { checked: boolean; partial: boolean; disabled: boolean }>();
    const visit = (node: TreeNode): { selectable: number; selected: number } => {
      const acc = node.data as FinancialAccount;
      let selectable = 0;
      let selected = 0;
      for (const child of node.children ?? []) {
        const counts = visit(child);
        selectable += counts.selectable;
        selected += counts.selected;
      }
      if (acc.isPostingAccount) {
        const isSelected = selectedIds.has(acc.id ?? '');
        const disabled = acc.isBlocked && !isSelected;
        states.set(acc.id ?? '', { checked: isSelected, partial: false, disabled });
        return { selectable: selectable + (disabled ? 0 : 1), selected: selected + (isSelected ? 1 : 0) };
      }
      states.set(acc.id ?? '', {
        checked: selectable > 0 && selected === selectable,
        partial: selected > 0 && selected < selectable,
        disabled: selectable === 0,
      });
      return { selectable, selected };
    };
    this.glAccountTreeNodes().forEach(visit);
    return states;
  });

  get isLoading() {
    return this.loading > 0;
  }

  get isEditMode() {
    return !_.isNil(this.itemMaster?.id) && !this.duplicateItemMaster;
  }

  get baseCode(): { label: string; code: string } | null {
    if (this.itemMaster?.baseYarnCode) {
      return { label: 'Base Yarn Code', code: this.itemMaster.baseYarnCode };
    }
    if (this.itemMaster?.baseFabricCode) {
      return {
        label: 'Base Fabric Code',
        code: this.itemMaster.baseFabricCode,
      };
    }
    if (this.itemMaster?.baseGreigeCode) {
      return {
        label: 'Base Greige Code',
        code: this.itemMaster.baseGreigeCode,
      };
    }
    if (this.itemMaster?.baseProductRecipeNumber) {
      return {
        label: 'Base Product Recipe',
        code: this.itemMaster.baseProductRecipeNumber,
      };
    }
    if (this.helpCreateItem && this.itemMasterProposal) {
      const code = this.itemMasterProposal.itemCode;
      switch (this.itemMasterProposal.mongoDBCollection) {
        case 'Yarn':
          return { label: 'Base Yarn Code', code };
        case 'Fabric':
          return { label: 'Base Fabric Code', code };
        case 'Greige':
          return { label: 'Base Greige Code', code };
        case 'ProductRecipe':
          return { label: 'Base Product Recipe', code };
      }
    }
    return null;
  }

  get subTitle() {
    if (this.itemMaster?.itemCode && !this.duplicateItemMaster) {
      return 'Code : ' + this.itemMaster.itemCode;
    } else if (this.itemMaster?.itemCode && this.duplicateItemMaster) {
      return 'Duplicate Code :' + this.itemMaster?.itemCode;
    }
    return '';
  }

  get suggestedName() {
    const firstSiteGroup = this.siteLocalNamesArray.controls[0];
    const firstSiteId = firstSiteGroup?.get('siteId')?.value as string | undefined;
    const categoryCodeValue = firstSiteGroup?.get('itemCategoryCode')?.value as string | undefined;
    const lowestSubCategoryValue = firstSiteGroup?.get('itemLowestSubCategoryCode')?.value as string | undefined;
    const siteCategories = firstSiteId ? (this.allItemCategoriesBySite[firstSiteId] ?? []) : [];

    const categoryName = this.localeHelperService.getLocalizedValue(siteCategories.find((ic) => ic.code === categoryCodeValue)?.name) ?? '';

    const itemSubCategory = siteCategories.find((ic) => ic.code === lowestSubCategoryValue) ?? null;

    let result = categoryName;
    if (itemSubCategory) {
      result += `:${this.localeHelperService.getLocalizedValue(itemSubCategory.name)}`;
      result += `;`;
    } else {
      result += `;`;
    }
    result += this.itemMasterProposal?.itemDescription ?? '';
    return result;
  }

  // ProductRecipe-driven items are chemical formulas — UOM must be kg.
  static readonly DEFAULT_CHEMICAL_INVENTORY_TYPE_ID = '6866c36b245e3504d98d3a47';
  static readonly UOM_LB = 3;
  static readonly UOM_KG = 0;

  private get isProductRecipeBased(): boolean {
    return (
      !_.isNil(this.itemMaster?.baseProductRecipeNumber) ||
      (this.helpCreateItem && this.itemMasterProposal?.mongoDBCollection === 'ProductRecipe')
    );
  }

  get showOnlyKg(): boolean {
    return this.isProductRecipeBased;
  }

  get showOnlyLbs() {
    if (this.isProductRecipeBased) {
      return false;
    }
    return (
      this.helpCreateItem ||
      !_.isNil(this.itemMaster?.baseFabricCode) ||
      !_.isNil(this.itemMaster?.baseYarnCode) ||
      !_.isNil(this.itemMaster?.baseGreigeCode)
    );
  }

  get uomRestrictValue(): number | undefined {
    if (this.showOnlyKg) return ItemMasterModalComponent.UOM_KG;
    if (this.showOnlyLbs) return ItemMasterModalComponent.UOM_LB;
    return undefined;
  }

  get yarns() {
    if (_.isNil(this.baseGreige)) {
      return [];
    }
    const yarns = this.baseGreige.yarnDetails.flatMap((yd) => yd.yarn).filter((y) => y !== null);
    if (yarns.length !== this.baseGreige.yarnDetails.length) {
      GlobalMessageUtil.error('Error', 'Greige yarn details are incomplete.');
      return [];
    }

    // Get unique yarns by yarn code and add to yarnCodesWithCertAndItemMaster
    const uniqueYarnCodes = new Set<string>();
    yarns.forEach((yarn) => {
      if (yarn && yarn.code && !uniqueYarnCodes.has(yarn.code)) {
        uniqueYarnCodes.add(yarn.code);

        if (!this.yarnCodesWithCertAndItemMaster.some((y) => y.yarnCode === yarn.code)) {
          this.yarnCodesWithCertAndItemMaster.push({
            yarnCode: yarn.code,
            certifications: [],
            itemMaster: null,
          });
        }
      }
    });

    // Return only unique yarns
    return yarns.filter((yarn, index, self) => yarn && yarn.code && index === self.findIndex((y) => y?.code === yarn.code));
  }

  getYarnCodeUsePercentage(yarnCode: string): number {
    if (!this.baseGreige) {
      return 0;
    }
    return this.baseGreige.yarnDetails.filter((yd) => yd.yarn?.code === yarnCode).reduce((sum, yd) => sum + (yd.usePercentage ?? 0), 0);
  }

  getYarnCodeWithCertAndItemMaster(yarnCode: string): YarnCodeWithCertAndItemMaster | null {
    return this.yarnCodesWithCertAndItemMaster.find((y) => y.yarnCode === yarnCode) || null;
  }

  getTotalUsePercentageForYarn(yarnCode: string): number {
    if (!this.baseGreige) {
      return 0;
    }
    return this.baseGreige.yarnDetails.filter((yd) => yd.yarn?.code === yarnCode).reduce((sum, yd) => sum + (yd.usePercentage ?? 0), 0);
  }

  private inventoryTypeProxyService = inject(InventoryTypeProxyService);
  private itemCategoryProxyService = inject(ItemCategoryProxyService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private yarnProxyService = inject(YarnProxyService);
  private greigeProxyService = inject(GreigeProxyService);
  private fabricProxyService = inject(FabricProxyService);
  private productRecipeProxyService = inject(ProductRecipeProxyService);
  public localeHelperService = inject(LocaleHelperService);
  private procurementItemCategoryProxyService = inject(ProcurementItemCategoryProxyService);
  private financialAccountProxyService = inject(FinancialAccountProxyService);
  private siteCompanyService = inject(SiteCompanyService);

  constructor(injector: Injector) {
    super(injector);
    this.statuses = this.enumService.getEnum('ItemStatus', 'itemEnums').values;
  }

  ngOnInit(): void {
    // Inventory types and item categories are loaded per-site by syncSiteLocalNames as
    // sites are added to the FormArray. No global preload needed here.
    this.getProcurementItemCategory();

    const siteIdsControl = this.itemMasterFormGroup.get('siteIds');
    siteIdsControl?.valueChanges.subscribe((value: string[] | null) => {
      this.syncSiteLocalNames(Array.isArray(value) ? value : []);
    });

    if (!this.canAssignSites) {
      const activeSiteId = SiteService.getSelectedSiteId() || '';
      if (activeSiteId) {
        siteIdsControl?.setValue([activeSiteId]);
      }
      siteIdsControl?.disable();
    }

    if (this.itemMaster) {
      this.patchItemMasterFormGroup();
      this.getBasePdItem(this.itemMaster);
    }

    this.loadGlAccountTree();

    if (!_.isNil(this.itemMasterProposal)) {
      if (this.itemMasterProposal.status === ItemMasterProposalStatus.Pending) {
        this.helpCreateItem = true;
        if (this.itemMasterProposal.mongoDBCollection === 'Greige') {
          const itemMaster: Partial<ItemMaster> = {
            baseGreigeCode: this.itemMasterProposal.itemCode,
          };
          this.getBasePdItem(itemMaster as ItemMaster);
        } else if (this.itemMasterProposal.mongoDBCollection === 'Yarn') {
          const itemMaster: Partial<ItemMaster> = {
            baseYarnCode: this.itemMasterProposal.itemCode,
          };
          this.getBasePdItem(itemMaster as ItemMaster);
        } else if (this.itemMasterProposal.mongoDBCollection === 'ProductRecipe') {
          const itemMaster: Partial<ItemMaster> = {
            baseProductRecipeNumber: this.itemMasterProposal.itemCode,
          };
          this.getBasePdItem(itemMaster as ItemMaster);
        }
      }
    }

    effect(() => {
      if (this.itemMasterProposal) {
        this.disableFileSection = false;
      } else {
        this.disableFileSection = true;
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.canEdit) {
      this.itemMasterFormGroup.disable();
      this.procurementItemCategoryReference.disable();
    }
  }

  propagateSuggestedName() {
    if (this.suggestedName) {
      this.itemMasterFormGroup.patchValue({
        itemDisplayName: {
          ...this.itemMasterFormGroup.get('itemDisplayName')?.value,
          en: this.suggestedName,
        },
        itemLongDisplayName: {
          ...this.itemMasterFormGroup.get('itemLongDisplayName')?.value,
          en: this.suggestedName,
        },
        itemDescription: {
          ...this.itemMasterFormGroup.get('itemDescription')?.value,
          en: this.suggestedName,
        },
      });
    }
  }

  getYarnCertifications(yarn: Yarn): Certification[] {
    return this.certifications.filter((cert) => {
      return cert.allCompositions || yarn.yarnComposition.some((comp) => cert.includedCompositions.includes(comp.compositionCode));
    });
  }

  onCertificationChange(event: MultiSelectChangeEvent, yarn: Yarn) {
    const certifications: Certification[] = event.value || [];
    const command: MatchingItemMasterCommand = {
      yarnCode: yarn!.code!,
      certificationReference: certifications.map((c) => c.id),
    };
    this.yarnProxyService.getItemMastersByYarnCode([command]).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const cert = this.getYarnCodeWithCertAndItemMaster(yarn!.code!);
          if (cert) {
            cert.itemMaster = response.data[yarn!.code!] || null;
            if (cert.itemMaster) {
              cert.itemMaster.fieldToDisplay =
                '(' + cert.itemMaster?.itemCode + ') ' + this.localeHelperService.getLocalizedValue(cert.itemMaster.itemDisplayName);
            }
          }
        } else {
          console.error('Error fetching item masters for yarn', response);
        }
      },
      error: (error) => {
        console.error('Error fetching item masters for yarn', error);
      },
    });
  }

  removeCertNames = (text: string): string => {
    let cleaned = text;

    cleaned = cleaned.replace(/\s*\[[^\]]*\]/g, '');

    return cleaned.trim().replace(/^,|,$/, '');
  };

  populateCertificationsToGeneralInfo() {
    const selected = this.selectedCertifications?.value || [];
    const selectedNames = selected.map((c) => c.name);
    const certText = selectedNames.length ? `[${selectedNames.join(', ')}]` : '';

    const clean = (val: string) => (val ? this.removeCertNames(val) : '');

    const currentDescEn = this.itemMasterFormGroup.get('itemDescription')?.value?.en || '';
    const currentLongNameEn = this.itemMasterFormGroup.get('itemLongDisplayName')?.value?.en || '';

    const newDescEn = certText ? `${clean(currentDescEn)}${currentDescEn ? ' ' : ''}${certText}`.trim() : clean(currentDescEn);

    const newLongNameEn = certText
      ? `${clean(currentLongNameEn)}${currentLongNameEn ? ' ' : ''}${certText}`.trim()
      : clean(currentLongNameEn);

    this.itemMasterFormGroup.patchValue({
      itemDescription: {
        ...this.itemMasterFormGroup.get('itemDescription')?.value,
        en: newDescEn,
      },
      itemLongDisplayName: {
        ...this.itemMasterFormGroup.get('itemLongDisplayName')?.value,
        en: newLongNameEn,
      },
    });
  }

  //#region Get Base PD elements
  public getBasePdItem(itemMaster: ItemMaster) {
    if (itemMaster.baseYarnCode) {
      this.loading++;
      this.yarnProxyService.getYarnByCode(itemMaster.baseYarnCode).subscribe({
        next: (response) => {
          this.loading--;
          if (response.success && response.data?.items) {
            this.baseYarn = response.data.items.find((y) => y.code === itemMaster.baseYarnCode) || null;
            this.selectedCertifications.setValue(
              this.certifications.filter((c) => this.itemMaster?.certificationReferences?.includes(c.id)),
            );
          }
        },
        error: (error) => {
          this.loading--;
          console.error('Error fetching base yarn', error);
        },
      });
    }

    if (itemMaster.baseFabricCode) {
      this.loading++;
      this.fabricProxyService.batchGetFabricsByCodes([itemMaster.baseFabricCode]).subscribe({
        next: (response) => {
          this.loading--;
          if (response.success && response.data) {
            this.baseFabric = response.data.find((f) => f.fabricCode === itemMaster.baseFabricCode) || null;
          }
        },
        error: (error) => {
          this.loading--;
          console.error('Error fetching base fabric', error);
        },
      });
    }

    if (itemMaster.baseGreigeCode) {
      this.loading++;
      this.greigeProxyService.batchGetGreigesByCodes([itemMaster.baseGreigeCode]).subscribe({
        next: (response) => {
          this.loading--;
          if (response.success && response.data) {
            this.baseGreige = response.data.find((g) => g.greigeCode === itemMaster.baseGreigeCode) || null;
            if ((_.isEmpty(itemMaster.relationships) || this.duplicateItemMaster) && this.baseGreige) {
              this.baseGreige.yarnDetails.forEach((yd) => {
                const event: Partial<MultiSelectChangeEvent> = {
                  value: [],
                };
                this.onCertificationChange(event as MultiSelectChangeEvent, yd.yarn!);
              });
            }
          }
        },
        error: (error) => {
          this.loading--;
          console.error('Error fetching base greige', error);
        },
      });

      const itemCodes = this.itemMaster?.relationships?.map((rel) => rel.itemMasterCode);

      if (itemCodes && itemCodes.length > 0 && !this.duplicateItemMaster) {
        this.loading++;
        this.itemMasterProxyService
          .getItemCodesBatch({
            itemCodes: itemCodes,
          })
          .subscribe({
            next: (response) => {
              this.loading--;
              if (response.success && response.data) {
                response.data.forEach((itemMaster) => {
                  const certifications = this.certifications.filter((c) => itemMaster.certificationReferences?.includes(c.id));

                  const existingYarnCode = this.yarnCodesWithCertAndItemMaster.find((y) => y.yarnCode === itemMaster.baseYarnCode);
                  if (existingYarnCode) {
                    existingYarnCode.certifications = certifications;
                    existingYarnCode.itemMaster = itemMaster;
                    // Set the display name for the item master
                    if (existingYarnCode.itemMaster) {
                      existingYarnCode.itemMaster.fieldToDisplay =
                        '(' +
                        existingYarnCode.itemMaster?.itemCode +
                        ') ' +
                        this.localeHelperService.getLocalizedValue(existingYarnCode.itemMaster.itemDisplayName);
                    }
                  } else {
                    // Create the item master entry if it doesn't exist
                    const newItemMaster = itemMaster;
                    newItemMaster.fieldToDisplay =
                      '(' + itemMaster?.itemCode + ') ' + this.localeHelperService.getLocalizedValue(itemMaster.itemDisplayName);

                    this.yarnCodesWithCertAndItemMaster.push({
                      yarnCode: itemMaster.baseYarnCode || '',
                      certifications: certifications,
                      itemMaster: newItemMaster,
                    });
                  }
                });
              }
            },
            error: (error) => {
              this.loading--;
              console.error('Error fetching item masters by yarn codes', error);
            },
          });
      }
    }

    if (itemMaster.baseProductRecipeNumber) {
      this.loading++;
      this.productRecipeProxyService.getByNumber(itemMaster.baseProductRecipeNumber).subscribe({
        next: (response) => {
          this.loading--;
          if (response.success && response.data) {
            this.baseProductRecipe = response.data;
          }
        },
        error: (error) => {
          this.loading--;
          console.error('Error fetching base product recipe', error);
        },
      });
    }
  }

  public getSupportingItemMasterForGreige(greige: Greige): void {
    const yarnBaseCodes = greige.yarnDetails
      .flatMap((yd) => yd.yarn)
      .map((yarn) => yarn?.code)
      .filter((code) => code !== undefined);

    const calls = yarnBaseCodes.map((code) => this.itemMasterProxyService.postItemSearch({ baseYarnCode: code }));

    this.loading++;
    calls.forEach((call) => {
      call.subscribe({
        next: (response) => {
          this.loading--;
          if (response.success && response.data?.items) {
            this.supportingItemMasters.push(...response.data.items);
          }
        },
        error: (error) => {
          this.loading--;
          console.error('Error fetching item masters by yarn codes', error);
        },
      });
    });
  }

  //#region Getters
  private clearMissingTranslations(field: 'itemDisplayName' | 'itemLongDisplayName' | 'itemDescription'): void {
    const langs: (keyof Translation)[] = ['es', 'en'];

    const translation = this.itemMaster?.[field];
    if (!translation) return;

    langs.forEach((lang) => {
      if (translation.missingTranslations?.includes(lang)) {
        translation[lang] = '';
      }
    });
  }

  patchItemMasterFormGroup() {
    //clearMissingTranslations values
    this.clearMissingTranslations('itemDisplayName');
    this.clearMissingTranslations('itemLongDisplayName');
    this.clearMissingTranslations('itemDescription');

    this.itemMasterFormGroup.patchValue({
      itemDisplayName: this.itemMaster?.itemDisplayName ?? null,
      itemLongDisplayName: this.itemMaster?.itemLongDisplayName ?? null,
      itemCode: this.itemMaster?.itemCode ?? null,
      itemDescription: this.itemMaster?.itemDescription ?? null,
      unitOfMeasure: this.itemMaster?.unitOfMeasure ?? null,
      itemClassificationCode: this.itemMaster?.itemClassificationCode ?? null,
      status: this.itemMaster?.status ?? null,
    });

    const siteIdsToLoad = this.canAssignSites
      ? (this.itemMaster?.siteIds ?? [])
      : [SiteService.getSelectedSiteId() || ''].filter((id) => id);
    const siteIdsControl = this.itemMasterFormGroup.get('siteIds');
    siteIdsControl?.setValue(siteIdsToLoad);
    if (!this.canAssignSites) {
      siteIdsControl?.disable();
    }

    this.procurementItemCategoryReference.setValue(this.itemMaster?.procurementItemCategoryReference ?? null);

    // Seed the persisted GL accounts (reference + number); names are filled once the chart loads.
    this.glAccounts.set(
      (this.itemMaster?.glAccounts ?? [])
        .filter((g) => !!g.reference)
        .map((g) => ({ reference: g.reference!, number: g.number ?? '', name: '' })),
    );
    this.showSelectedGlOnly.set(this.glAccounts().length > 0);

    if (this.isEditMode) {
      // InventoryType is per-site now and should not be edited from the modal once an item exists.
      this.siteLocalNamesArray.controls.forEach((group) => {
        group.get('inventoryTypeReference')?.disable();
      });
    }
  }

  // Pre-fill UOM and inventory type for brand-new ProductRecipe-driven proposals.
  // Only runs when the modal is creating a fresh ItemMaster from a Pending proposal —
  // existing ItemMasters keep whatever the user already saved.
  private applyProductRecipeDefaults(): void {
    if (!this.helpCreateItem || this.itemMasterProposal?.mongoDBCollection !== 'ProductRecipe') {
      return;
    }
    if (this.itemMaster) {
      return;
    }

    this.itemMasterFormGroup.patchValue({ unitOfMeasure: ItemMasterModalComponent.UOM_KG });

    const defaultInventoryTypeId = ItemMasterModalComponent.DEFAULT_CHEMICAL_INVENTORY_TYPE_ID;
    // For each site group, seed the inventory type if the option is available for that site.
    this.siteLocalNamesArray.controls.forEach((group) => {
      const siteId = group.get('siteId')?.value as string | undefined;
      if (!siteId) return;
      const options = this.inventoryTypeOptionsBySite[siteId] ?? [];
      if (options.some((it) => it.id === defaultInventoryTypeId)) {
        group.get('inventoryTypeReference')?.setValue(defaultInventoryTypeId);
      }
    });
  }

  //#endregion

  _setOptionsForPrimeNg(value: EnumValues, restrictTo?: number): { label: string; value: number }[] {
    const options = Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
    if (typeof restrictTo === 'number') {
      return options.filter((opt) => opt.value === restrictTo);
    }
    return options;
  }

  onSave() {
    this.submittedForm = true;

    if (!_.isNil(this.baseGreige)) {
      this.yarnCodesWithCertAndItemMaster.every((yarnCodeWithCert) => {
        if (_.isNil(yarnCodeWithCert.itemMaster)) {
          GlobalMessageUtil.error(
            'Error',
            this.translateService.instant('ItemMaster_Yarn_ItemMaster_Not_Found', {
              yarnCode: yarnCodeWithCert.yarnCode,
            }),
          );
          return false; // Stop processing if any yarn item master is not found
        } else {
          return true; // Continue processing if all yarn item masters are found
        }
      });
    }

    if (this.itemMasterFormGroup.valid) {
      const isUpdate = !_.isNil(this.itemMaster?.id) && !this.duplicateItemMaster;
      const itemMasterCode = isUpdate ? this.itemMaster!.itemCode : undefined;
      const id = isUpdate ? this.itemMaster!.id : undefined;

      const relationships: ItemRelationship[] | undefined = !_.isNil(this.baseGreige)
        ? this.yarnCodesWithCertAndItemMaster.map((yarnCodeWithCert) => {
            // Sum all use percentages for the same yarn code
            const totalUsePercentage = this.baseGreige!.yarnDetails.filter((yd) => yd.yarn?.code === yarnCodeWithCert.yarnCode).reduce(
              (sum, yd) => sum + (yd.usePercentage ?? 0),
              0,
            );

            const relationship: ItemRelationship = {
              itemMasterCode: yarnCodeWithCert.itemMaster!.itemCode,
              relationshipType: ItemRelationshipType.Composition,
              quantity: totalUsePercentage,
              unitOfMeasure: Uom.pct,
            };
            return relationship;
          })
        : undefined;

      const rawValue = this.itemMasterFormGroup.getRawValue();
      const formSiteIds: string[] = Array.isArray(rawValue.siteIds) ? rawValue.siteIds : [];
      const formSitePropertyEntries: {
        siteId: string;
        en: string;
        es: string;
        inventoryTypeReference?: string;
        itemCategoryCode?: string;
        itemLowestSubCategoryCode?: string;
      }[] = Array.isArray(rawValue.siteLocalNames) ? rawValue.siteLocalNames : [];
      const formItemDisplayName = (rawValue.itemDisplayName ?? {}) as { en?: string; es?: string };
      delete rawValue.siteLocalNames;
      delete rawValue.siteIds;

      const sitePropertyMap = new Map<string, (typeof formSitePropertyEntries)[number]>();
      for (const entry of formSitePropertyEntries) {
        sitePropertyMap.set(entry.siteId, entry);
      }

      const payloadSiteProperties: SiteProperty[] = formSiteIds.map((siteId) => {
        const fromForm = sitePropertyMap.get(siteId);
        const en = fromForm?.en?.trim() ? fromForm.en : (formItemDisplayName.en ?? '');
        const es = fromForm?.es?.trim() ? fromForm.es : (formItemDisplayName.es ?? '');
        return {
          siteId,
          itemLocalName: { en, es },
          inventoryTypeReference: fromForm?.inventoryTypeReference || undefined,
          itemCategoryCode: fromForm?.itemCategoryCode || undefined,
          itemLowestSubCategoryCode: fromForm?.itemLowestSubCategoryCode || undefined,
        };
      });
      const payloadSiteIds: string[] | undefined = this.canAssignSites ? formSiteIds : undefined;

      const itemMaster: ItemMaster = {
        id: id,
        itemCode: itemMasterCode,
        ...rawValue,
        baseFabricCode: this.helpCreateItem
          ? this.helpCreateItem && this.itemMasterProposal?.mongoDBCollection === 'Fabric'
            ? this.itemMasterProposal?.itemCode
            : undefined
          : this.itemMaster?.baseFabricCode,
        baseYarnCode: this.helpCreateItem
          ? this.helpCreateItem && this.itemMasterProposal?.mongoDBCollection === 'Yarn'
            ? this.itemMasterProposal?.itemCode
            : undefined
          : this.itemMaster?.baseYarnCode,
        baseGreigeCode: this.helpCreateItem
          ? this.helpCreateItem && this.itemMasterProposal?.mongoDBCollection === 'Greige'
            ? this.itemMasterProposal?.itemCode
            : undefined
          : this.itemMaster?.baseGreigeCode,
        baseProductRecipeNumber: this.helpCreateItem
          ? this.helpCreateItem && this.itemMasterProposal?.mongoDBCollection === 'ProductRecipe'
            ? this.itemMasterProposal?.itemCode
            : undefined
          : this.itemMaster?.baseProductRecipeNumber,
        certificationReferences: _.isEmpty(this.selectedCertifications.value)
          ? undefined
          : this.selectedCertifications.value.map((c) => c.id),
        relationships: relationships,
        procurementItemCategoryReference: this.procurementItemCategoryReference.value ?? undefined,
        glAccounts: this.glAccounts().length ? this.glAccounts().map((a) => ({ reference: a.reference, number: a.number })) : undefined,
        siteIds: payloadSiteIds,
        siteProperties: payloadSiteProperties,
      };

      this.loading++;
      this.itemMasterProxyService
        .postItemMaster(itemMaster, this.itemMasterProposal?.id ?? undefined, this.itemRequestReference ?? undefined)
        .subscribe({
          next: ({ data, success }) => {
            this.loading--;

            if (success == true) {
              this.itemMaster = data;

              if (!this.itemRequestReference) {
                // Always return the API response. The backend projects per-site fields
                // (inventoryTypeReference, itemCategoryCode, itemLowestSubCategoryCode)
                // from SiteProperties into the flat DTO — the locally-built `itemMaster`
                // doesn't have those top-level fields, which left listing-table columns
                // empty after edit until a manual refresh.
                this.close({ action: 'save', data });
              }

              if (!itemMaster.id) {
                GlobalMessageUtil.success(
                  'Success',
                  `${this.translateService.instant('ItemMaster_Successfully').replace('{{name}}', data?.itemCode || '')}`,
                );
              } else {
                GlobalMessageUtil.success(
                  'Success',
                  `${this.translateService
                    .instant('ItemMaster_Updated_Successfully')
                    .replace('{{name}}', this.itemMaster?.id ? this.itemMaster.itemCode : '')}`,
                );
              }
            }
          },
          error: () => {
            this.loading--;
          },
        });
    }
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  handleDelete(event: { item: MasterData; confirmCallback: () => void; cancelCallback: () => void }) {
    MasterDataActionUtil.attemptToDeleteMasterData(event.item).subscribe({
      next: (confirmed) => {
        if (confirmed) {
          event.confirmCallback();
        } else {
          event.cancelCallback();
        }
      },
    });
  }

  showMessagesApi(event: ApiResponse<MasterData>) {
    if (event.success) {
      GlobalMessageUtil.success('Success', this.translateService.instant('ItemMaster_Record_Deleted'));
    } else {
      GlobalMessageUtil.error(
        'Error',
        (typeof event.errorMessage === 'string' ? event.errorMessage : getLocalizedValue(this.translateService, event.errorMessage)) ||
          this.translateService.instant('ItemMaster_Record_Error'),
      );
    }
  }

  private getProcurementItemCategory() {
    this.procurementItemCategoryProxyService.getAllItemsCategories().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const res = response.data.map((item) => ({
            id: item.id,
            name: this.localeHelperService.getLocalizedValue(item.name),
          }));

          this.procurementItemCategoryOptions = res;
        }
      },
      error: (error) => {
        console.error('Error fetching procurement item categories', error);
      },
    });
  }

  //#region GL Accounts
  // Resolve the selected site's financial company, fetch its chart of accounts, and build the
  // checkbox tree. In edit mode the tree drives the persisted selection; in read-only mode we only
  // use it to fill display names on the already-saved accounts.
  private loadGlAccountTree(): void {
    // Skip the chart-of-accounts fetch entirely when the section is hidden (e.g. prod). Any
    // already-saved accounts seeded into `glAccounts` are preserved on save.
    if (!this.showGlAccountsSection) return;
    const siteId = SiteService.getSelectedSiteId();
    this.loadingGlAccountTree.set(true);
    this.siteCompanyService
      .getFinancialCompanyId(siteId)
      .pipe(switchMap((companyId) => (companyId ? this.financialAccountProxyService.getByCompany(companyId) : of(null))))
      .subscribe({
        next: (response) => {
          const roots = this.buildGlAccountTree(response?.data ?? []);
          if (this.canEdit) {
            this.syncGlAccountsWithChart();
            this.expandToSelectedGlAccountNodes();
          } else {
            this.fillGlAccountNames();
          }
          this.glAccountTreeNodes.set(roots);
          this.loadingGlAccountTree.set(false);
        },
        error: () => {
          this.glAccountTreeNodes.set([]);
          this.loadingGlAccountTree.set(false);
        },
      });
  }

  private buildGlAccountTree(accounts: FinancialAccount[]): TreeNode[] {
    this.glAccountNodeById.clear();
    const childrenByParent = new Map<string, FinancialAccount[]>();
    const roots: FinancialAccount[] = [];
    for (const acc of accounts) {
      const parent = acc.parentAccountId;
      if (parent) {
        const list = childrenByParent.get(parent) ?? [];
        list.push(acc);
        childrenByParent.set(parent, list);
      } else {
        roots.push(acc);
      }
    }

    const byNumber = (a: FinancialAccount, b: FinancialAccount) => (a.accountNumber ?? '').localeCompare(b.accountNumber ?? '');
    const toNode = (acc: FinancialAccount): TreeNode => {
      const children = (childrenByParent.get(acc.id ?? '') ?? []).sort(byNumber).map(toNode);
      const node: TreeNode = {
        key: acc.id,
        data: acc,
        leaf: children.length === 0,
        children: children.length ? children : undefined,
      };
      if (acc.id) this.glAccountNodeById.set(acc.id, node);
      return node;
    };
    return roots.sort(byNumber).map(toNode);
  }

  // Auto-expand every ancestor of a pre-selected account so the checked rows are visible on open
  // without the user drilling down through the hierarchy.
  private expandToSelectedGlAccountNodes(): void {
    for (const account of this.glAccounts()) {
      let parentId = (this.glAccountNodeById.get(account.reference)?.data as FinancialAccount | undefined)?.parentAccountId ?? null;
      while (parentId) {
        const parentNode = this.glAccountNodeById.get(parentId);
        if (!parentNode) break;
        parentNode.expanded = true;
        parentId = (parentNode.data as FinancialAccount | undefined)?.parentAccountId ?? null;
      }
    }
  }

  // Edit mode: keep only the saved accounts that are posting accounts of the loaded chart, with their
  // current number and name.
  private syncGlAccountsWithChart(): void {
    const seen = new Set<string>();
    this.glAccounts.update((rows) =>
      rows.flatMap((r) => {
        const acc = this.glAccountNodeById.get(r.reference)?.data as FinancialAccount | undefined;
        if (!acc?.isPostingAccount || seen.has(r.reference)) return [];
        seen.add(r.reference);
        return [this.toGlAccountRow(acc)];
      }),
    );
  }

  private toGlAccountRow(acc: FinancialAccount): { reference: string; number: string; name: string } {
    return { reference: acc.id ?? '', number: acc.accountNumber, name: this.localeHelperService.getLocalizedValue(acc.accountName) };
  }

  // Posting accounts in a subtree, including the node itself.
  private collectPostingAccounts(node: TreeNode): FinancialAccount[] {
    const acc = node.data as FinancialAccount;
    const nested = (node.children ?? []).flatMap((child) => this.collectPostingAccounts(child));
    return acc.isPostingAccount ? [acc, ...nested] : nested;
  }

  // Read-only mode: fill display names/numbers on the saved accounts from the loaded chart.
  private fillGlAccountNames(): void {
    this.glAccounts.update((rows) =>
      rows.map((r) => {
        const acc = this.glAccountNodeById.get(r.reference)?.data as FinancialAccount | undefined;
        return acc ? { ...r, number: r.number || acc.accountNumber, name: this.localeHelperService.getLocalizedValue(acc.accountName) } : r;
      }),
    );
  }

  getGlAccountName(account: FinancialAccount): string {
    return this.localeHelperService.getLocalizedValue(account.accountName);
  }

  // A posting account toggles only itself; a header account toggles the posting accounts under it in the
  // full chart (blocked ones are never added).
  toggleGlAccount(account: FinancialAccount, checked: boolean): void {
    const node = this.glAccountNodeById.get(account.id ?? '');
    if (!node) return;
    const targets = account.isPostingAccount ? [account] : this.collectPostingAccounts(node);
    const targetIds = new Set(targets.map((a) => a.id));
    this.glAccounts.update((rows) => {
      const kept = rows.filter((r) => !targetIds.has(r.reference));
      return checked ? [...kept, ...targets.filter((a) => !a.isBlocked).map((a) => this.toGlAccountRow(a))] : kept;
    });
    if (!this.glAccounts().length) this.showSelectedGlOnly.set(false);
  }

  // Keep only selected posting accounts and the ancestor nodes on their path. Ancestor nodes are
  // shallow-cloned with pruned children and force-expanded so the path is visible.
  private pruneGlTreeToSelected(nodes: TreeNode[], selectedIds: Set<string>): TreeNode[] {
    const result: TreeNode[] = [];
    for (const node of nodes) {
      const id = (node.data as FinancialAccount | undefined)?.id;
      const prunedChildren = node.children ? this.pruneGlTreeToSelected(node.children, selectedIds) : [];
      if (id && selectedIds.has(id)) {
        result.push(node);
      } else if (prunedChildren.length) {
        result.push({ ...node, children: prunedChildren, expanded: true });
      }
    }
    return result;
  }
  //#endregion

  onFileChange(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      this.selectedFiles = Array.from(files);
    }
  }

  uploadFile() {
    if (!this.itemMaster) {
      GlobalMessageUtil.warn('Warning', this.translateService.instant('ItemMaster_No_Item_Master'));
      return;
    }

    if (this.selectedFiles.length === 0) {
      GlobalMessageUtil.warn('Warning', 'Please select a file first');
      return;
    }

    const params = {
      itemCode: this.itemMaster.itemCode,
      isMain: 1,
    };

    this.isUploading = true;

    this.itemMasterProxyService.uploadFiles(params, this.selectedFiles).subscribe({
      next: () => {
        this.uploadedFiles.push(...this.selectedFiles);

        GlobalMessageUtil.success('Success', this.translateService.instant('Files are uploaded'));

        this.selectedFiles = [];
        this.isUploading = false;
      },
      error: () => {
        this.isUploading = false;
      },
    });
  }
  // please not delete it's fine for implement in the future  Att: Ivan Perez
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
